import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ExcluirCadastroBtn } from "@/components/ExcluirCadastroBtn";

export default function Montadoras() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = () => api.get("/montadoras").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/montadoras", {
        ...form,
        nome: form.razao_social || form.nome_fantasia || form.nome,
        area_atuacao: form.area_atuacao.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Montadora cadastrada");
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="label-cap">Cadastros · MontaJá</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Montadoras</h1>
          <p className="text-xs text-muted-foreground mt-1">Gestoras operacionais regionais — parceiras da plataforma.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-sharp brand-bg text-white hover:bg-[#001A5E]" data-testid="btn-nova-montadora">+ Nova montadora</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nova montadora</DialogTitle>
              <DialogDescription>Cadastro completo da montadora regional parceira da plataforma.</DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="identificacao">
              <TabsList className="rounded-none">
                <TabsTrigger value="identificacao" data-testid="mt-tab-id">Identificação</TabsTrigger>
                <TabsTrigger value="endereco" data-testid="mt-tab-end">Endereço</TabsTrigger>
                <TabsTrigger value="contatos" data-testid="mt-tab-cont">Contatos</TabsTrigger>
                <TabsTrigger value="responsaveis" data-testid="mt-tab-resp">Responsáveis</TabsTrigger>
                <TabsTrigger value="acesso" data-testid="mt-tab-acc">Acesso</TabsTrigger>
              </TabsList>

              <TabsContent value="identificacao" className="space-y-4 mt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="Razão social" v={form.razao_social} on={(v) => setForm(f => ({...f, razao_social: v}))} testid="mt-razao" />
                  <FF label="Nome fantasia" v={form.nome_fantasia} on={(v) => setForm(f => ({...f, nome_fantasia: v}))} testid="mt-fantasia" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="CPF/CNPJ" v={form.cpf_cnpj} on={(v) => setForm(f => ({...f, cpf_cnpj: v}))} testid="mt-doc" />
                  <div>
                    <Label className="label-cap">Status do CNPJ</Label>
                    <Select value={form.status_cnpj} onValueChange={(v) => setForm(f => ({...f, status_cnpj: v}))}>
                      <SelectTrigger className="mt-2" data-testid="mt-status-cnpj"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="inativo">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <FF label="Área de atuação (cidades, vírgula)" v={form.area_atuacao} on={(v) => setForm(f => ({...f, area_atuacao: v}))} testid="mt-area" />
              </TabsContent>

              <TabsContent value="endereco" className="space-y-4 mt-4">
                <FF label="Endereço completo" v={form.endereco} on={(v) => setForm(f => ({...f, endereco: v}))} testid="mt-end" />
                <div className="grid md:grid-cols-3 gap-4">
                  <FF label="Bairro" v={form.bairro} on={(v) => setForm(f => ({...f, bairro: v}))} testid="mt-bairro" />
                  <FF label="Cidade" v={form.cidade} on={(v) => setForm(f => ({...f, cidade: v}))} testid="mt-cidade" />
                  <FF label="Estado" v={form.estado} on={(v) => setForm(f => ({...f, estado: v}))} testid="mt-estado" />
                </div>
              </TabsContent>

              <TabsContent value="contatos" className="space-y-4 mt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="Telefone comercial" v={form.telefone_comercial} on={(v) => setForm(f => ({...f, telefone_comercial: v}))} testid="mt-tel-com" />
                  <FF label="Telefone operacional" v={form.telefone_operacional} on={(v) => setForm(f => ({...f, telefone_operacional: v}))} testid="mt-tel-op" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="WhatsApp" v={form.whatsapp} on={(v) => setForm(f => ({...f, whatsapp: v}))} testid="mt-wpp" />
                  <FF label="PIX" v={form.pix} on={(v) => setForm(f => ({...f, pix: v}))} testid="mt-pix" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="E-mail administrativo" v={form.email_administrativo} on={(v) => setForm(f => ({...f, email_administrativo: v}))} testid="mt-email-adm" />
                  <FF label="Contato cliente (suporte cliente final)" v={form.contato_cliente} on={(v) => setForm(f => ({...f, contato_cliente: v}))} testid="mt-cli" />
                </div>
              </TabsContent>

              <TabsContent value="responsaveis" className="space-y-4 mt-4">
                <FF label="Responsável legal" v={form.responsavel_legal} on={(v) => setForm(f => ({...f, responsavel_legal: v}))} testid="mt-resp-legal" />
                <FF label="Responsável operacional" v={form.responsavel_operacional} on={(v) => setForm(f => ({...f, responsavel_operacional: v}))} testid="mt-resp-op" />
              </TabsContent>

              <TabsContent value="acesso" className="space-y-4 mt-4">
                <p className="text-xs text-muted-foreground">Credenciais de acesso ao painel (opcional na criação).</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="E-mail de login" v={form.email} on={(v) => setForm(f => ({...f, email: v}))} testid="mt-email" />
                  <FF label="Senha" v={form.password} on={(v) => setForm(f => ({...f, password: v}))} testid="mt-senha" type="password" />
                </div>
              </TabsContent>
            </Tabs>

            <Button onClick={submit} className="w-full btn-sharp brand-bg text-white hover:bg-[#001A5E] mt-4" data-testid="mt-salvar">Salvar</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-[#F4F4F5]">
            <tr><Th>Razão social</Th><Th>Nome fantasia</Th><Th>CPF/CNPJ</Th><Th>Cidade</Th><Th>Área</Th><Th>Responsável op.</Th><Th>Ações</Th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Nenhuma montadora cadastrada</td></tr>}
            {list.map((m) => (
              <tr key={m.id} className="border-b border-border last:border-0" data-testid={`montadora-row-${m.id}`}>
                <Td><strong>{m.razao_social || m.nome}</strong>{m.status_cnpj === "inativo" && <span className="ml-2 px-1.5 text-[10px] bg-[#E60000] text-white">CNPJ inativo</span>}</Td>
                <Td>{m.nome_fantasia || "—"}</Td>
                <Td className="font-mono text-xs">{m.cpf_cnpj}</Td>
                <Td>{m.cidade || "—"}</Td>
                <Td>{(m.area_atuacao || []).slice(0, 3).join(", ")}</Td>
                <Td>{m.responsavel_operacional || m.responsavel || "—"}</Td>
                <Td>
                  <ExcluirCadastroBtn endpoint={`/admin/montadoras/${m.id}`} nome={m.razao_social || m.nome}
                    onExcluido={load} testid={`montadora-excluir-${m.id}`} />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function emptyForm() {
  return {
    razao_social: "", nome_fantasia: "", nome: "", cpf_cnpj: "", status_cnpj: "ativo",
    endereco: "", bairro: "", cidade: "", estado: "", area_atuacao: "",
    telefone_comercial: "", telefone_operacional: "", whatsapp: "", pix: "",
    email_administrativo: "", contato_cliente: "",
    responsavel_legal: "", responsavel_operacional: "",
    email: "", password: "",
  };
}
function FF({ label, v, on, testid, type="text" }) {
  return (
    <div>
      <Label className="label-cap">{label}</Label>
      <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)} className="mt-2" data-testid={testid} />
    </div>
  );
}
function Th({ children }) { return <th className="text-left p-3 label-cap font-medium">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
